import React, { useEffect, useRef } from 'react'
import { FaUtensils } from 'react-icons/fa'

const FilterPanel = ({
  showFilters,
  filterByCreator,
  setFilterByCreator,
  filterByRating,
  setFilterByRating,
  filterByCookingTime,
  setFilterByCookingTime,
  filterByIngredients,
  setFilterByIngredients,
  ingredientInput,
  setIngredientInput,
  handleAddIngredientTag,
  handleRemoveIngredientTag,
  handleIngredientKeyDown,
  uniqueCreators,
  uniqueIngredients,
  showCreatorDropdown,
  setShowCreatorDropdown,
  showRatingDropdown,
  setShowRatingDropdown,
  showCookingTimeDropdown,
  setShowCookingTimeDropdown,
  handleResetFilters,
  showOnlyFavorites,
}) => {
  const creatorDropdownRef = useRef(null)
  const cookingTimeDropdownRef = useRef(null)

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close creator dropdown if clicked outside
      if (
        creatorDropdownRef.current &&
        !creatorDropdownRef.current.contains(event.target)
      ) {
        setShowCreatorDropdown(false)
      }

      // Close cooking time dropdown if clicked outside
      if (
        cookingTimeDropdownRef.current &&
        !cookingTimeDropdownRef.current.contains(event.target)
      ) {
        setShowCookingTimeDropdown(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [setShowCreatorDropdown, setShowCookingTimeDropdown])

  if (!showFilters) return null

  return (
    <div className="bg-white p-0 rounded-lg transition-all duration-300">
      <div className="grid grid-cols-1 lg:grid-cols-2 lg:grid-cols-4 gap-4 p-4 pb-2">
        {/* Creator filter */}
        <div>
          <div className="flex justify-between items-center mb-1">
            <label className="block text-sm font-medium text-gray-700">
              Creator
            </label>
            {filterByCreator && (
              <button
                onClick={() => setFilterByCreator('')}
                className="text-xs text-gray-500 hover:text-gray-700"
              >
                Clear
              </button>
            )}
          </div>
          <select
            value={filterByCreator}
            onChange={(e) => setFilterByCreator(e.target.value)}
            className="hidden"
          >
            <option value="">Any Creator</option>
            {uniqueCreators.map((creator) => (
              <option
                key={creator.id}
                value={creator.id}
                className="flex items-center"
              >
                {creator.name || 'Unknown'}
              </option>
            ))}
          </select>

          {/* Custom dropdown for creators with images */}
          <div className="relative mt-1" ref={creatorDropdownRef}>
            <div
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 cursor-pointer flex items-center z-100"
              onClick={() => setShowCreatorDropdown(!showCreatorDropdown)}
            >
              {filterByCreator ? (
                <>
                  {uniqueCreators.find((c) => c.id === filterByCreator)
                    ?.image_url && (
                    <img
                      src={
                        uniqueCreators.find((c) => c.id === filterByCreator)
                          ?.image_url
                      }
                      alt={`Profile of ${uniqueCreators.find((c) => c.id === filterByCreator)?.name || 'creator'}`}
                      className="w-6 h-6 rounded-full mr-2 object-cover"
                    />
                  )}
                  <span>
                    {uniqueCreators.find((c) => c.id === filterByCreator)
                      ?.name || 'Unknown'}
                  </span>
                </>
              ) : (
                <span className="text-gray-500">All</span>
              )}
              <svg
                className="w-5 h-5 ml-auto text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </div>

            {showCreatorDropdown && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-80 overflow-auto">
                <div
                  className="px-3 py-2 cursor-pointer hover:bg-gray-100"
                  onClick={() => {
                    setFilterByCreator('')
                    setShowCreatorDropdown(false)
                  }}
                >
                  All
                </div>
                {uniqueCreators.map((creator) => (
                  <div
                    key={creator.id}
                    className="px-3 py-2 cursor-pointer hover:bg-gray-100 flex items-center"
                    onClick={() => {
                      setFilterByCreator(creator.id)
                      setShowCreatorDropdown(false)
                    }}
                  >
                    {creator.image_url && (
                      <img
                        src={creator.image_url}
                        alt={`Profile of ${creator.name || 'creator'}`}
                        className="w-6 h-6 rounded-full mr-2 object-cover"
                      />
                    )}
                    <span>{creator.name || 'Unknown'}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Rating filter */}
        <div>
          <div className="flex justify-between items-center mb-1">
            <label className="block text-sm font-medium text-gray-700">
              Minimum Rating
            </label>
            {filterByRating > 0 && (
              <button
                onClick={() => setFilterByRating(0)}
                className="text-xs text-gray-500 hover:text-gray-700"
              >
                Clear
              </button>
            )}
          </div>
          <select
            value={filterByRating}
            onChange={(e) => setFilterByRating(Number(e.target.value))}
            className="hidden"
          >
            <option value="0">All</option>
            <option value="1">1+</option>
            <option value="1.5">1.5+</option>
            <option value="2">2+</option>
            <option value="2.5">2.5+</option>
            <option value="3">3+</option>
            <option value="3.5">3.5+</option>
            <option value="4">4+</option>
            <option value="4.5">4.5+</option>
            <option value="5">5</option>
          </select>

          {/* Rating scale for filtering */}
          <div className="relative mt-1">
            <div className="w-full px-3 py-2 border border-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 flex items-center h-[42px]">
              <div className="flex items-center w-full">
                {/* Rating display */}
                {filterByRating > 0 ? (
                  <span className="text-sm mr-3 text-gray-700 min-w-[50px]">
                    Min: {filterByRating}
                  </span>
                ) : (
                  <span className="text-sm mr-3 text-gray-500 min-w-[50px]">
                    All
                  </span>
                )}

                {/* Rating bar container */}
                <div className="relative flex-grow">
                  {/* Background bar */}
                  <div
                    className="h-6 w-full bg-gray-100 rounded-md overflow-hidden cursor-pointer hover:bg-gray-200 relative"
                    onClick={(e) => {
                      const rect = e.currentTarget.getBoundingClientRect()
                      const x = e.clientX - rect.left
                      const width = rect.width

                      // Calculate position as percentage (0-1)
                      const position = Math.max(0, Math.min(1, x / width)) * 100

                      // Map position to rating (1-5 with 0.5 increments)
                      // Numbers are at 10%, 30%, 50%, 70%, 90%
                      // Half ratings are at 20%, 40%, 60%, 80%

                      // Find the closest rating point
                      const ratings = [1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5]
                      const positions = [10, 20, 30, 40, 50, 60, 70, 80, 90]

                      // Find the closest position
                      let closestIndex = 0
                      let minDistance = Math.abs(position - positions[0])

                      for (let i = 1; i < positions.length; i++) {
                        const distance = Math.abs(position - positions[i])
                        if (distance < minDistance) {
                          minDistance = distance
                          closestIndex = i
                        }
                      }

                      setFilterByRating(ratings[closestIndex])
                    }}
                  >
                    {/* Filled bar based on current filter */}
                    <div
                      className="absolute inset-y-0 left-0 bg-yellow-200 transition-all duration-200"
                      style={{
                        width:
                          filterByRating === 0
                            ? '0%'
                            : filterByRating === 5
                              ? '100%'
                              : // Calculate exact position based on rating
                                `${filterByRating * 20 - 10}%`,
                      }}
                    />

                    {/* Numbers directly in the bar */}
                    <div className="absolute inset-0 flex items-center justify-between px-3">
                      {[1, 2, 3, 4, 5].map((num) => (
                        <div
                          key={num}
                          className="text-xs font-medium text-gray-600 cursor-pointer hover:text-gray-900 z-10 flex items-center justify-center hover:text-gray-900 transition-colors"
                          onClick={(e) => {
                            e.stopPropagation()
                            setFilterByRating(num)
                          }}
                        >
                          <FaUtensils
                            className={`h-4 w-4 ${
                              num <= filterByRating
                                ? 'text-yellow-500'
                                : 'text-gray-400'
                            }`}
                          />
                        </div>
                      ))}
                    </div>

                    {/* Invisible click areas for half ratings */}
                    <div className="absolute inset-0 flex items-center">
                      {[1.5, 2.5, 3.5, 4.5].map((halfRating) => (
                        <div
                          key={halfRating}
                          className="h-full cursor-pointer"
                          style={{
                            position: 'absolute',
                            left: `${halfRating * 20 - 10 - 10}%`,
                            width: '20%',
                          }}
                          onClick={(e) => {
                            e.stopPropagation()
                            setFilterByRating(halfRating)
                          }}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Cooking time filter */}
        <div>
          <div className="flex justify-between items-center mb-1">
            <label className="block text-sm font-medium text-gray-700">
              Cooking Time
            </label>
            {filterByCookingTime && (
              <button
                onClick={() => setFilterByCookingTime('')}
                className="text-xs text-gray-500 hover:text-gray-700"
              >
                Clear
              </button>
            )}
          </div>
          <select
            value={filterByCookingTime}
            onChange={(e) => setFilterByCookingTime(e.target.value)}
            className="hidden"
          >
            <option value="">All</option>
            <option value="15">15 minutes or less</option>
            <option value="30">30 minutes or less</option>
            <option value="45">45 minutes or less</option>
            <option value="60">1 hour or less</option>
            <option value="120">2 hours or less</option>
          </select>

          {/* Custom dropdown for cooking time */}
          <div className="relative mt-1" ref={cookingTimeDropdownRef}>
            <div
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 cursor-pointer flex items-center z-100"
              onClick={() =>
                setShowCookingTimeDropdown(!showCookingTimeDropdown)
              }
            >
              {filterByCookingTime ? (
                <span className="flex items-center">
                  {filterByCookingTime === '60'
                    ? '1 hour or less'
                    : filterByCookingTime === '120'
                      ? '2 hours or less'
                      : `${filterByCookingTime} minutes or less`}
                </span>
              ) : (
                <span className="text-gray-500">All</span>
              )}
              <svg
                className="w-5 h-5 ml-auto text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </div>

            {showCookingTimeDropdown && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-80 overflow-auto">
                <div
                  className="px-3 py-2 cursor-pointer hover:bg-gray-100"
                  onClick={() => {
                    setFilterByCookingTime('')
                    setShowCookingTimeDropdown(false)
                  }}
                >
                  All
                </div>
                {[
                  { value: '15', label: '15 minutes or less' },
                  { value: '30', label: '30 minutes or less' },
                  { value: '45', label: '45 minutes or less' },
                  { value: '60', label: '1 hour or less' },
                  { value: '120', label: '2 hours or less' },
                ].map((option) => (
                  <div
                    key={option.value}
                    className="px-3 py-2 cursor-pointer hover:bg-gray-100"
                    onClick={() => {
                      setFilterByCookingTime(option.value)
                      setShowCookingTimeDropdown(false)
                    }}
                  >
                    {option.label}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Ingredient filter - updated to multi-ingredient tags */}
        <div>
          <div className="flex justify-between items-center mb-1">
            <label className="block text-sm font-medium text-gray-700">
              Contains All These Ingredients
            </label>
            {filterByIngredients.length > 0 && (
              <button
                onClick={() => setFilterByIngredients([])}
                className="text-xs text-gray-500 hover:text-gray-700"
              >
                Clear
              </button>
            )}
          </div>
          <div className="relative">
            <div
              className={`flex flex-wrap items-center gap-1 w-full px-2 py-1.5 border border-gray-300 rounded-md focus-within:ring-2 focus-within:ring-green-500 focus-within:border-green-500 ${filterByIngredients.length > 0 ? 'bg-gray-50' : ''}`}
            >
              {/* Ingredient tags */}
              {filterByIngredients.map((ingredient, index) => (
                <div
                  key={index}
                  className="flex items-center bg-gray-200 text-gray-800 text-sm rounded-md px-2 py-1"
                >
                  <span>{ingredient}</span>
                  <button
                    type="button"
                    className="ml-1.5 text-gray-600 hover:text-gray-800"
                    onClick={() => handleRemoveIngredientTag(ingredient)}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-3.5 w-3.5"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                </div>
              ))}

              {/* Input field */}
              <input
                type="text"
                value={ingredientInput}
                onChange={(e) => setIngredientInput(e.target.value)}
                onKeyDown={handleIngredientKeyDown}
                onBlur={() => {
                  if (ingredientInput.trim() !== '') {
                    handleAddIngredientTag()
                  }
                }}
                placeholder={
                  filterByIngredients.length > 0
                    ? ''
                    : 'Enter ingredients (press Tab to add)'
                }
                className="flex-grow min-w-[120px] border-0 p-1 focus:ring-0 focus:outline-none bg-transparent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-search-cancel-button]:appearance-none [&::-webkit-calendar-picker-indicator]:appearance-none"
                list="ingredient-options"
              />
            </div>
          </div>
          <datalist id="ingredient-options">
            {uniqueIngredients.map((ingredient, index) => (
              <option key={index} value={ingredient} />
            ))}
          </datalist>
        </div>
      </div>

      {/* Clear All Filters button - only shown when at least TWO filters are active */}
      {((filterByCreator && filterByRating > 0) ||
        (filterByCreator && filterByCookingTime) ||
        (filterByCreator && filterByIngredients.length > 0) ||
        (filterByCreator && showOnlyFavorites) ||
        (filterByRating > 0 && filterByCookingTime) ||
        (filterByRating > 0 && filterByIngredients.length > 0) ||
        (filterByRating > 0 && showOnlyFavorites) ||
        (filterByCookingTime && filterByIngredients.length > 0) ||
        (filterByCookingTime && showOnlyFavorites) ||
        (filterByIngredients.length > 0 && showOnlyFavorites)) && (
        <div className="flex justify-center p-2">
          <button
            onClick={handleResetFilters}
            className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-3.5 w-3.5"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
            Clear All Filters
          </button>
        </div>
      )}
    </div>
  )
}

export default FilterPanel
